import React from 'react';
import {Box, keyframes} from '@mui/material';
import {t} from '../../themes/statamicTokens';

const pulse = keyframes`
    0%   { opacity: 1; }
    50%  { opacity: 0.55; }
    100% { opacity: 1; }
`;

const baseSx = {
    borderRadius: `${t.radius.sm}px`,
    backgroundColor: t.color.sidebarHover,
    animation: `${pulse} 1.4s ease-in-out infinite`,
};

export const SkeletonLine = ({width = '100%', height = 12, sx}) => (
    <Box sx={{...baseSx, width, height, ...sx}}/>
);

export const SkeletonCircle = ({size = 28, sx}) => (
    <Box sx={{...baseSx, width: size, height: size, borderRadius: '50%', ...sx}}/>
);

export const SkeletonLines = ({count = 3, widths}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
        {Array.from({length: count}).map((_, i) => (
            <SkeletonLine
                key={i}
                width={widths?.[i] ?? (i === count - 1 ? '55%' : '100%')}
                height={10}
            />
        ))}
    </Box>
);

export const SkeletonTableRow = ({columns = 5, hasCheckbox = true, hasActions = true}) => (
    <Box component="tr" sx={{height: t.table.rowHeight}}>
        {hasCheckbox && (
            <Box component="td" sx={{px: 1.5, borderBottom: `1px solid ${t.color.border.hairline}`}}>
                <SkeletonLine width={16} height={16} sx={{borderRadius: `${t.radius.sm}px`}}/>
            </Box>
        )}
        {Array.from({length: columns}).map((_, i) => (
            <Box
                key={i}
                component="td"
                sx={{px: `${t.table.cellPaddingX}px`, borderBottom: `1px solid ${t.color.border.hairline}`}}
            >
                {i === 0 ? (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        <SkeletonCircle size={32}/>
                        <SkeletonLine width={140} height={10}/>
                    </Box>
                ) : (
                    <SkeletonLine width={i % 2 ? 80 : 120} height={10}/>
                )}
            </Box>
        ))}
        {hasActions && (
            <Box component="td" sx={{pr: 1.5, borderBottom: `1px solid ${t.color.border.hairline}`}}>
                <SkeletonCircle size={24}/>
            </Box>
        )}
    </Box>
);

export const SkeletonList = ({rows = 5, columns = 5, hasCheckbox = true, hasActions = true}) => (
    <>
        {Array.from({length: rows}).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} hasCheckbox={hasCheckbox} hasActions={hasActions}/>
        ))}
    </>
);

export const SkeletonCard = ({aspect = '4 / 3'}) => (
    <Box
        sx={{
            backgroundColor: t.color.surface,
            border: `1px solid ${t.color.border.default}`,
            borderRadius: `${t.radius.md}px`,
            overflow: 'hidden',
        }}
    >
        <Box sx={{...baseSx, aspectRatio: aspect, borderRadius: 0}}/>
        <Box sx={{p: 1.5, display: 'flex', flexDirection: 'column', gap: 1}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <SkeletonCircle size={24}/>
                <SkeletonLine width="60%" height={10}/>
            </Box>
            <SkeletonLines count={2} widths={['100%', '70%']}/>
        </Box>
    </Box>
);
